import { Enterprise, PricingCacheEntrySchema, } from "@buildwithflux/models";
import { clientConverterFromZod, clientConverterFromZodWithIdField, wrapClientConverterWithIdField } from "../converter";
import { leaseSchema } from "../lease";
import { FirestoreAdapter } from "./FirestoreAdapter";
/**
 * Firestore access for the non-admin client.
 */
export class ClientFirestoreAdapter extends FirestoreAdapter {
    /** @inheritDoc */
    batch() {
        return this.db.batch();
    }
    /** @inheritDoc */
    knowledgeBaseNotesCollection() {
        return this.db
            .collection("copilotKnowledgeBase")
            .withConverter(this.converters.unsafeConverterFactory());
    }
    /** @inheritDoc */
    knowledgeBaseSuggestionsCollection() {
        return this.db
            .collection("copilotKnowledgeBaseSuggestions")
            .withConverter(this.converters.unsafeConverterFactory());
    }
    systemKnowledgeModerationQueueCollection() {
        return this.db
            .collection("copilotSystemKnowledgeModerationQueue")
            .withConverter(this.converters.unsafeConverterFactory());
    }
    /** @inheritDoc */
    creditAccountSettingsCollection() {
        return this.db
            .collection("creditAccountSettings")
            .withConverter(this.converters.unsafeConverterFactory());
    }
    /** @inheritDoc */
    creditPeriodSummaryCollection() {
        return this.db
            .collection("creditPeriodSummaries")
            .withConverter(this.converters.unsafeConverterFactory());
    }
    /** @inheritDoc */
    creditLedgerEntryCollection(accountUid, period) {
        return this.creditPeriodSummary(accountUid, period)
            .collection("creditLedgerEntries")
            .withConverter(this.converters.unsafeConverterFactory());
    }
    /** @inheritDoc */
    documentCollection(strategy) {
        return this.db
            .collection("documents")
            .withConverter(this.converters.documentConverterFactory(strategy));
    }
    /**
     * Document action records are a little special - when we retrieve them from the adapter, we actually also get their
     * metadata.  This is important for remote sync!
     */
    documentActionRecordsCollection(documentUid) {
        return this.document(documentUid)
            .collection("actionRecords")
            .withConverter(this.converters.actionRecordConverter);
    }
    documentActiveUsersCollection(documentUid) {
        return this.document(documentUid)
            .collection("active_users")
            .withConverter(this.converters.unsafeWithSubcollectionOverwriteConverterFactory());
    }
    documentAssetsCollection(documentUid) {
        return this.document(documentUid)
            .collection("assets")
            .withConverter(this.converters.unsafeWithSubcollectionOverwriteConverterFactory());
    }
    documentProjectDocsCollection(documentUid) {
        return this.document(documentUid)
            .collection("project_docs")
            .withConverter(this.converters.unsafeWithSubcollectionOverwriteConverterFactory());
    }
    documentConfigsCollection(documentUid) {
        return this.document(documentUid)
            .collection("configs")
            .withConverter(this.converters.unsafeWithSubcollectionOverwriteConverterFactory());
    }
    documentUserUiStatesCollection(documentUid) {
        return this.document(documentUid)
            .collection("userUiStates")
            .withConverter(this.converters.unsafeWithSubcollectionOverwriteConverterFactory());
    }
    documentCommentsCollection(documentUid) {
        return this.document(documentUid)
            .collection("comments")
            .withConverter(this.converters.unsafeWithSubcollectionOverwriteConverterFactory());
    }
    documentCommentLikesCollection(documentUid, commentUid) {
        return this.documentCommentsCollection(documentUid)
            .doc(commentUid)
            .collection("likes")
            .withConverter(this.converters.unsafeConverterFactory());
    }
    documentCommentThreadsCollection(documentUid) {
        return this.document(documentUid)
            .collection("comment_threads")
            .withConverter(this.converters.unsafeWithSubcollectionOverwriteConverterFactory());
    }
    documentChatThreadsCollection(documentUid) {
        return this.document(documentUid)
            .collection("chat_threads")
            .withConverter(this.converters.unsafeWithSubcollectionOverwriteConverterFactory());
    }
    documentChatMessagesCollection(documentUid, threadUid, _metadataStrategy) {
        // Note: metadataStrategy is not used in the client adapter as custom metadata
        // strategies are only supported in the admin adapter for managed service writes
        return this.document(documentUid)
            .collection("chat_threads")
            .doc(threadUid)
            .collection("messages")
            .withConverter(this.converters.unsafeWithSubcollectionOverwriteConverterFactory());
    }
    documentChatMessageReactionsCollection(documentUid, threadUid, messageUid) {
        return this.document(documentUid)
            .collection("chat_threads")
            .doc(threadUid)
            .collection("messages")
            .doc(messageUid)
            .collection("reactions")
            .withConverter(this.converters.unsafeWithSubcollectionOverwriteConverterFactory());
    }
    documentElementsCollection(documentUid) {
        return this.document(documentUid)
            .collection("elements")
            .withConverter(this.converters.elementConverter);
    }
    documentNetsCollection(documentUid) {
        return this.document(documentUid)
            .collection("nets")
            .withConverter(this.converters.unsafeWithSubcollectionOverwriteConverterFactory());
    }
    documentPcbLayoutNodesCollection(documentUid) {
        return this.document(documentUid)
            .collection("pcbLayoutNodes")
            .withConverter(this.converters.unsafeWithSubcollectionOverwriteConverterFactory());
    }
    documentPcbLayoutRuleSetsCollection(documentUid) {
        return this.document(documentUid)
            .collection("pcbLayoutRuleSets")
            .withConverter(this.converters.unsafeWithSubcollectionOverwriteConverterFactory());
    }
    documentPropertiesCollection(documentUid) {
        return this.document(documentUid)
            .collection("properties")
            .withConverter(this.converters.propertyConverter);
    }
    documentRolesCollection(documentUid) {
        return this.document(documentUid)
            .collection("roles")
            .withConverter(this.converters.unsafeWithSubcollectionOverwriteConverterFactory());
    }
    documentInvitesCollection(documentUid) {
        return this.document(documentUid)
            .collection("invites")
            .withConverter(this.converters.unsafeWithSubcollectionOverwriteConverterFactory());
    }
    documentRouteVerticesCollection(documentUid) {
        return this.document(documentUid)
            .collection("routeVertices")
            .withConverter(this.converters.unsafeWithSubcollectionOverwriteConverterFactory());
    }
    documentRoutesCollection(documentUid) {
        return this.document(documentUid)
            .collection("routes")
            .withConverter(this.converters.unsafeWithSubcollectionOverwriteConverterFactory());
    }
    documentSuggestionsCollection(documentUid) {
        return this.document(documentUid)
            .collection("suggestions")
            .withConverter(this.converters.unsafeWithSubcollectionOverwriteConverterFactory());
    }
    documentPricingCollection(documentUid) {
        return this.document(documentUid).collection("pricing");
    }
    documentElementMPNCollection() {
        return this.db.collection("documentManufacturerPartNumbers");
    }
    reviewCollection(documentUid) {
        return this.document(documentUid)
            .collection("reviews")
            .withConverter(this.converters.unsafeWithSubcollectionOverwriteConverterFactory());
    }
    /** @inheritDoc */
    enterpriseCollection() {
        return this.db
            .collection("enterprises")
            .withConverter(clientConverterFromZodWithIdField(Enterprise, "uid"));
    }
    leasesCollection() {
        return this.db.collection("leases").withConverter(clientConverterFromZod(leaseSchema));
    }
    /**
     * @inheritDoc
     * @see firebase.firestore.Firestore.runTransaction
     */
    runTransaction(updateFunction) {
        return this.db.runTransaction(updateFunction);
    }
    /** @inheritdoc */
    starredDocumentCollection() {
        return this.db
            .collection("starredDocuments")
            .withConverter(this.converters.unsafeConverterFactory());
    }
    /** @inheritdoc */
    partCollection() {
        return this.db.collection("partsV4").withConverter(this.converters.unsafeConverterFactory());
    }
    /** @inheritDoc */
    partVersionCollection(partUid) {
        return this.part(partUid)
            .collection("versions")
            .withConverter(this.converters.partVersionConverter);
    }
    pricingDataCacheCollection() {
        return this.db
            .collection("pricingDataCache")
            .withConverter(clientConverterFromZod(PricingCacheEntrySchema));
    }
    /** @inheritDoc */
    userCollection() {
        return this.db
            .collection("users")
            .withConverter(wrapClientConverterWithIdField(this.converters.unsafeConverterFactory(), "handle"));
    }
    /** @inheritDoc */
    accountFollowersCollection() {
        return this.db
            .collection("accountFollowers")
            .withConverter(this.converters.unsafeConverterFactory());
    }
    /** @inheritDoc */
    handleMappingCollection() {
        return this.db.collection("usernames").withConverter(this.converters.handleMappingConverter);
    }
    /** @inheritDoc */
    userPrivateMetadataCollection() {
        return this.db
            .collection("usersPrivateMetadata")
            .withConverter(wrapClientConverterWithIdField(this.converters.unsafeToPlainMigrationConverterFactory(), "uid"));
    }
    /** @inheritDoc */
    userSentinelsCollection(handle) {
        return this.user(handle)
            .collection("sentinels")
            .withConverter(this.converters.unsafeConverterFactory());
    }
    /** @inheritDoc */
    organizationSentinelsCollection(orgUid) {
        return this.organization(orgUid)
            .collection("sentinels")
            .withConverter(this.converters.unsafeConverterFactory());
    }
    markdownConversionsCollection() {
        return this.db
            .collection("markdownConversion")
            .withConverter(this.converters.unsafeWithSubcollectionOverwriteConverterFactory());
    }
    datasheetVectorizationsCollection() {
        return this.db
            .collection("datasheetVectorization")
            .withConverter(this.converters.unsafeWithSubcollectionOverwriteConverterFactory());
    }
    /** @inheritDoc */
    externalConnectorAuthCollection() {
        return this.db.collection("externalConnectorAuth");
    }
    /** @inheritDoc */
    organizationCollection() {
        return this.db
            .collection("organizations")
            .withConverter(wrapClientConverterWithIdField(this.converters.unsafeConverterFactory(), "uid"));
    }
    /** @inheritDoc */
    organizationMembersCollection(organizationUid) {
        return this.organization(organizationUid)
            .collection("organizationMembers")
            .withConverter(wrapClientConverterWithIdField(this.converters.unsafeConverterFactory(), "userUid"));
    }
    /** @inheritDoc */
    organizationMembersCollectionGroup() {
        return this.db
            .collectionGroup("organizationMembers")
            .withConverter(this.converters.unsafeConverterFactory());
    }
    /** @inheritDoc */
    organizationPrivateMetadataCollection() {
        return this.db
            .collection("organizationsPrivateMetadata")
            .withConverter(wrapClientConverterWithIdField(this.converters.unsafeConverterFactory(), "uid"));
    }
    /** @inheritDoc */
    userAnalyticsCollection() {
        return this.db
            .collection("userAnalytics")
            .withConverter(this.converters.unsafeConverterFactory());
    }
    /** @inheritDoc */
    userJobsCollection(userHandle) {
        return this.user(userHandle)
            .collection("jobs")
            .withConverter(this.converters.unsafeConverterFactory());
    }
    /** @inheritDoc */
    userJobGenerationsCollection(userHandle, jobUid) {
        return this.user(userHandle)
            .collection("jobs")
            .doc(jobUid)
            .collection("generations")
            .withConverter(this.converters.unsafeConverterFactory());
    }
    /** @inheritDoc */
    userJobStateHistoryCollection(userHandle, jobUid) {
        return this.user(userHandle)
            .collection("jobs")
            .doc(jobUid)
            .collection("stateHistory")
            .withConverter(this.converters.unsafeConverterFactory());
    }
    /** @inheritDoc */
    documentDatasheetMetadataCollection() {
        return this.db
            .collection("documentDatasheetMetadata")
            .withConverter(this.converters.unsafeConverterFactory());
    }
    /** @inheritDoc */
    chatThreadsCollectionGroup() {
        return this.db
            .collectionGroup("chat_threads")
            .withConverter(this.converters.unsafeWithSubcollectionOverwriteConverterFactory());
    }
    /** @inheritDoc */
    rolesCollectionGroup() {
        return this.db
            .collectionGroup("roles")
            .withConverter(this.converters.unsafeWithSubcollectionOverwriteConverterFactory());
    }
    /** @inheritDoc */
    internalAccountsSeoBanCollection() {
        return this.db
            .collection("internal")
            .doc("accounts")
            .collection("seo")
            .withConverter(this.converters.unsafeConverterFactory());
    }
    /** @inheritDoc */
    internalAccountsVipCollection() {
        return this.db
            .collection("internal")
            .doc("accounts")
            .collection("vip")
            .withConverter(this.converters.unsafeConverterFactory());
    }
    /** @inheritDoc */
    internalAccountsLifecycleCollection() {
        return this.db
            .collection("internal")
            .doc("accounts")
            .collection("lifecycle")
            .withConverter(this.converters.unsafeConverterFactory());
    }
    /** @inheritDoc */
    internalAccountsEducationalDomainsCollection() {
        return this.db
            .collection("internal")
            .doc("accounts")
            .collection("educationalDomains")
            .withConverter(this.converters.unsafeConverterFactory());
    }
    /** @inheritDoc */
    internalAccountsEducationalQualificationCollection() {
        return this.db
            .collection("internal")
            .doc("accounts")
            .collection("educationalQualification")
            .withConverter(this.converters.unsafeConverterFactory());
    }
}
//# sourceMappingURL=ClientFirestoreAdapter.js.map